import { App, Divider, Form, Upload } from 'antd';
import { useIntl } from 'react-intl';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { listPreDeliveryNote } from '../../../../../../services/api/preDeliveryNote';
import { listDeliveryNote } from '../../../../../../services/api/deliveryNote';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import getFieldsDictionary from './fieldsDictionary';
import PropTypes from 'prop-types';
import RegionSelect from '../../../../../../components/RegionSelect/RegionSelect';
import { MainContext } from '../../../../../../context/MainContext';
import {
  createFileOnBackend,
  getFile,
} from '../../../../../../services/api/files';
import fileIcon from '../../../../../../style/img/file.png';
import { NotificationsContext } from '../../../../../../context/NotificationsContext';
import {
  readRegionV2,
  readRegion as readRegionV1,
} from '../../../../../../services/api/ocr';
import {
  calculateLimitTime,
  formatFormData,
  PRE_DELIVERY_NOTES_ATTRS,
  PRE_DELIVERY_NOTES_ATTRS_CEMEX,
  PRE_DELIVERY_NOTES_MASH_CONTROL_ATTRS,
  processFieldValue,
  recreateRegions,
} from './utils.index';

import styles from './index.module.scss';
import AccionaButton from '../../../../../../components/AccionaButton';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { updateDeliveryNotesFileExtensionsButton } from '../../../../../../constants/general';
import FieldInput from './components/FieldInput';
import ReloadIcon from '../../../../../../components/ReloadIcon';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

const ScannedDocumentsLayout = ({
  initialData,
  fieldGroups,
  onBack,
  mode = 'create',
  validableFields,
  onFinish,
  header,
  topFormFields,
  model,
  isAssayedAndLotified = false,
}) => {
  const { notification } = App.useApp();
  const [loadingFields, setLoadingFields] = useState({});
  const [form] = Form.useForm();
  const { project } = useOutletContext();
  const { setLoading, loading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const intl = useIntl();
  const location = useLocation();
  const timer = useRef();
  const { fileName, validated } = useParams();
  const isXlsxFile = fileName?.includes('xlsx');
  const [selectedFile, setSelectedFile] = useState();
  const [elementsLoaded, setElementsLoaded] = useState();
  const [regions, setRegions] = useState();
  const [selectedRegion, setSelectedRegion] = useState();
  const [moveAllRegions, setMoveAllRegions] = useState(false);
  const [unLockedRegion, setUnLockedRegion] = useState('');
  const [currentFileName, setCurrentFileName] = useState(fileName);
  const [camImageName, setCamImageName] = useState();
  const queryParams = new URLSearchParams(location.search);
  const hasValidatedParam = queryParams.has('validated');
  const [validableFieldsStatus, setValidableFieldsStatus] = useState(
    Object.fromEntries(
      validableFields.map((f) => [f, mode === 'edit' && hasValidatedParam]),
    ),
  );
  const validableFieldsStatusRef = useRef(validableFieldsStatus);
  const fieldsConfig = getFieldsDictionary(
    model,
    intl,
    project.normative_data.name,
    isAssayedAndLotified,
  );
  const isANoValidatedAndXlsxDeliveryNote =
    validated === 'noValidated' && fileName.includes('xlsx');

  const hasNoRegions = !regions?.length;

  const onClickValidate = (fieldName, validated) => {
    setValidableFieldsStatus((prevStatus) => ({
      ...prevStatus,
      [fieldName]: validated,
    }));
    validableFieldsStatusRef.current = {
      ...validableFieldsStatusRef.current,
      [fieldName]: validated,
    };
  };

  const areAllFieldsManuallyValidated = useCallback(() => {
    return validableFields.every(
      (f) => validableFieldsStatusRef.current[f] === true,
    );
  }, [validableFields]);

  const validateFormFieldsAfterPredeliveryUpdate = useCallback(
    async (formData) => {
      if (!formData || Object.keys(formData).length === 0) return false;

      const showValidationNotification = (errorCount, unvalidatedCount) => {
        let description = '';
        if (errorCount > 0) {
          description = intl.formatMessage(
            { id: 'validations.initialCheck.errors.withUnvalidated' },
            { errorCount, unvalidatedCount },
          );
        } else if (unvalidatedCount > 0) {
          description = intl.formatMessage(
            { id: 'validations.initialCheck.errors.onlyUnvalidated' },
            { unvalidatedCount },
          );
        }
        if (description) {
          notification.warning({
            message: intl.formatMessage({
              id: 'validations.initialCheck.errors.title',
            }),
            description,
            duration: 6,
            placement: 'topRight',
          });
        }
      };

      try {
        await form.validateFields();
        // Check manual validation
        const fieldErrors = form.getFieldsError();
        // Si hay errores, poner validableFieldsStatus en false para esos campos
        fieldErrors.forEach((f) => {
          if (f.errors.length > 0 && validableFields.includes(f.name[0])) {
            setValidableFieldsStatus((prevStatus) => ({
              ...prevStatus,
              [f.name[0]]: false,
            }));
            validableFieldsStatusRef.current[f.name[0]] = false;
          }
        });
        if (!areAllFieldsManuallyValidated()) {
          const errorCount = fieldErrors.filter((f) => f.errors.length).length;
          const unvalidatedCount = validableFields.filter(
            (f) => !validableFieldsStatusRef.current[f],
          ).length;
          showValidationNotification(errorCount, unvalidatedCount);
          return false;
        }
        return true;
      } catch (error) {
        setTimeout(() => {
          const fieldErrors = form.getFieldsError();
          fieldErrors.forEach((f) => {
            if (f.errors.length > 0 && validableFields.includes(f.name[0])) {
              setValidableFieldsStatus((prevStatus) => ({
                ...prevStatus,
                [f.name[0]]: false,
              }));
              validableFieldsStatusRef.current[f.name[0]] = false;
            }
          });
          const errorCount = fieldErrors.filter((f) => f.errors.length).length;
          const unvalidatedCount = validableFields.filter(
            (f) => !validableFieldsStatusRef.current[f],
          ).length;
          showValidationNotification(errorCount, unvalidatedCount);
          return false;
        }, 500);
      }
    },
    [areAllFieldsManuallyValidated, form, intl, validableFields, notification],
  );

  const searchAndUpdatePreRegisteredDoc = useCallback(
    async (value) => {
      if (!value) return;
      const pid = project.id_project;
      const usedPreRegistered = location.pathname.includes('readDocument');
      const notValidated = location.pathname.includes('noValidated');
      const isXlsxFile = fileName?.includes('xlsx');
      const isCemosaFile = fileName?.includes('cemosa');
      if (model.includes('delivery')) {
        listPreDeliveryNote({
          page: 1,
          size: 5,
          filters: {
            pid: project.id_project,
            docket_number: value,
            used: usedPreRegistered || notValidated ? 0 : 1,
          },
        }).then((result) => {
          if (isXlsxFile) {
            form.setFieldValue('supplier_id', 'Cemex');
          }
          if (result?.data?.data.length > 0) {
            const preDeliveryNote = result.data.data[0];
            if (preDeliveryNote.docket_number === Number(value)) {
              setCamImageName(preDeliveryNote.image_name);
              PRE_DELIVERY_NOTES_ATTRS.forEach((attr) => {
                if (attr === 'arriving_date' && preDeliveryNote[attr]) {
                  form.setFieldValue(
                    attr,
                    processFieldValue(attr, preDeliveryNote[attr]),
                  );
                } else {
                  if (attr.includes('time')) {
                    const [hours, minutes] = preDeliveryNote[attr].split(':');
                    preDeliveryNote[attr] = `${hours}:${minutes}`;
                  }
                  form.setFieldValue(
                    attr,
                    processFieldValue(attr, preDeliveryNote[attr]),
                  );
                }
              });
              if (preDeliveryNote.model === 'cemex') {
                PRE_DELIVERY_NOTES_ATTRS_CEMEX.forEach((attr) => {
                  if (attr.includes('time')) {
                    const [hours, minutes] = preDeliveryNote[attr].split(':');
                    preDeliveryNote[attr] = `${hours}:${minutes}`;
                  }
                  form.setFieldValue(
                    attr,
                    processFieldValue(attr, preDeliveryNote[attr]),
                  );
                });
              }
              if (preDeliveryNote['mash_control'])
                PRE_DELIVERY_NOTES_MASH_CONTROL_ATTRS.forEach((attr) => {
                  form.setFieldValue(
                    attr,
                    processFieldValue(attr, preDeliveryNote[attr]),
                  );
                });
              form.setFieldValue(
                'elements',
                processFieldValue('elements', preDeliveryNote.elements),
              );
            }
            const { docket_number, ...formFields } = form.getFieldsValue(true);
            validateFormFieldsAfterPredeliveryUpdate(formFields);
          }
        });
        listDeliveryNote(
          {
            filters: { docket_number: value, project: pid },
          },
          model,
        ).then((result) => {
          if (result?.data?.data.length > 0) {
            const currentDocket_number =
              initialData?.regionsValue?.docket_number?.toString();
            if (value?.toString() === currentDocket_number && mode === 'edit') {
              form.setFields([{ name: 'docket_number', errors: [] }]);
              form.validateFields(['docket_number']);
            } else {
              form.setFields([
                {
                  name: 'docket_number',
                  errors: [intl.formatMessage({ id: 'docket_number.inDB' })],
                },
              ]);
            }
          } else {
            form.setFields([{ name: 'docket_number', errors: [] }]);
            form.validateFields(['docket_number']);
          }
        });
      } else {
        listDeliveryNote({
          filters: { docket_number: value, project: pid },
        }).then((result) => {
          if (isCemosaFile) {
            form.setFieldValue('supplier_id', 'Cemosa');
          }
          if (result?.data?.data.length === 0) {
            form.setFields([
              {
                name: 'docket_number',
                errors: [intl.formatMessage({ id: 'docket_number.notInDB' })],
              },
            ]);
          } else {
            form.setFields([{ name: 'docket_number', errors: [] }]);
            form.validateFields(['docket_number']);
          }
        });
      }
    },
    [
      project.id_project,
      location.pathname,
      fileName,
      model,
      form,
      validateFormFieldsAfterPredeliveryUpdate,
      initialData?.regionsValue?.docket_number,
      mode,
      intl,
    ],
  );

  const validateInitialData = useCallback(
    async (formData) => {
      if (!formData || Object.keys(formData).length === 0) return false;

      const docketNumber = formData['docket_number'];
      const showValidationNotification = (errorCount, unvalidatedCount) => {
        let description = '';
        if (errorCount > 0) {
          description = intl.formatMessage(
            { id: 'validations.initialCheck.errors.withUnvalidated' },
            { errorCount, unvalidatedCount },
          );
        } else if (unvalidatedCount > 0) {
          description = intl.formatMessage(
            { id: 'validations.initialCheck.errors.onlyUnvalidated' },
            { unvalidatedCount },
          );
        }
        if (description) {
          notification.warning({
            message: intl.formatMessage({
              id: 'validations.initialCheck.errors.title',
            }),
            description,
            duration: 6,
            placement: 'topRight',
          });
        }
      };

      try {
        await form.validateFields();
        if (docketNumber) {
          await searchAndUpdatePreRegisteredDoc(docketNumber);
          await form.validateFields(['docket_number']).catch(() => {
            setValidableFieldsStatus((prevStatus) => ({
              ...prevStatus,
              docket_number: false,
            }));
          });
        }
        // Check manual validation
        const fieldErrors = form.getFieldsError();
        // Si hay errores, poner validableFieldsStatus en false para esos campos
        fieldErrors.forEach((f) => {
          if (f.errors.length > 0 && validableFields.includes(f.name[0])) {
            setValidableFieldsStatus((prevStatus) => ({
              ...prevStatus,
              [f.name[0]]: false,
            }));
            validableFieldsStatusRef.current[f.name[0]] = false;
          }
        });
        if (!areAllFieldsManuallyValidated()) {
          const errorCount = fieldErrors.filter((f) => f.errors.length).length;
          const unvalidatedCount = validableFields.filter(
            (f) => !validableFieldsStatusRef.current[f],
          ).length;
          showValidationNotification(errorCount, unvalidatedCount);
          return false;
        }
        return true;
      } catch (error) {
        // Validation failed
        if (docketNumber) {
          await searchAndUpdatePreRegisteredDoc(docketNumber);
        }
        setTimeout(() => {
          const fieldErrors = form.getFieldsError();
          fieldErrors.forEach((f) => {
            if (f.errors.length > 0 && validableFields.includes(f.name[0])) {
              setValidableFieldsStatus((prevStatus) => ({
                ...prevStatus,
                [f.name[0]]: false,
              }));
              validableFieldsStatusRef.current[f.name[0]] = false;
            }
          });
          const errorCount = fieldErrors.filter((f) => f.errors.length).length;
          const unvalidatedCount = validableFields.filter(
            (f) => !validableFieldsStatusRef.current[f],
          ).length;
          showValidationNotification(errorCount, unvalidatedCount);
          return false;
        }, 500);
      }
    },
    [
      areAllFieldsManuallyValidated,
      form,
      intl,
      searchAndUpdatePreRegisteredDoc,
      validableFields,
      notification,
    ],
  );

  const updateMoveAllRegions = () => {
    if (moveAllRegions) {
      setValidableFieldsStatus((prevStatus) => ({
        ...prevStatus,
        ...Object.fromEntries(validableFields.map((f) => [f, false])),
      }));
      validableFieldsStatusRef.current = {
        ...validableFieldsStatusRef.current,
        ...Object.fromEntries(validableFields.map((f) => [f, false])),
      };
      readMandatoryRegions();
      setMoveAllRegions(false);
    }
  };

  const readRegion = useCallback(
    async (region) => {
      setUnLockedRegion('');
      setLoadingFields((prev) => ({ ...prev, [region.data]: true }));
      if (region.data === 'compressive_strength') {
        const result = await readRegionV2(fileName, region);
        form.setFieldValue(region.data, processFieldValue(region.data, result));
        setLoadingFields((prev_1) => ({ ...prev_1, [region.data]: false }));
        return { field: region.data, result };
      } else {
        const result = await readRegionV1(fileName, region);
        form.setFieldValue(region.data, processFieldValue(region.data, result));
        setLoadingFields((prev_2) => ({ ...prev_2, [region.data]: false }));
        return { field: region.data, result: result };
      }
    },
    [fileName, form],
  );

  const readRegions = useCallback(
    (regions) => {
      const loadingObj = {};
      regions.forEach((reg) => {
        loadingObj[reg.data] = true;
      });
      setLoadingFields((prev) => ({ ...prev, ...loadingObj }));

      const ocrPromises = regions.map((reg) => readRegion(reg));

      Promise.allSettled(ocrPromises).then((results) => {
        const loadingOff = {};
        regions.forEach((reg) => {
          loadingOff[reg.data] = false;
        });
        setLoadingFields((prev) => ({ ...prev, ...loadingOff }));
        setLoading(false);

        const manufactureTimeWasRead = results.some(
          (r) =>
            r.status === 'fulfilled' && r.value.field === 'manufacture_time',
        );

        if (manufactureTimeWasRead) {
          const manufactureTime = form.getFieldValue('manufacture_time');
          if (manufactureTime) {
            const calculatedLimitTime = calculateLimitTime(manufactureTime);
            if (calculatedLimitTime) {
              form.setFieldValue('limit_time', calculatedLimitTime);
            }
          }
        }

        validateInitialData(
          results.reduce((acc, r) => {
            if (r.status === 'fulfilled') {
              acc[r.value.field] = r.value.result;
            }
            return acc;
          }, {}),
        );
      });
    },
    [setLoading, validateInitialData, readRegion, form],
  );

  const readMandatoryRegions = useCallback(() => {
    const mandatoryRegions = elementsLoaded.regions.filter((reg) =>
      fieldGroups.mandatory.includes(reg.data),
    );
    readRegions(mandatoryRegions);
  }, [elementsLoaded?.regions, fieldGroups.mandatory, readRegions]);

  const readOtherRegions = () => {
    const otherRegions = elementsLoaded.regions.filter((reg) =>
      fieldGroups.other.includes(reg.data),
    );
    readRegions(otherRegions);
  };

  const onClickLock = (data) => {
    if (!unLockedRegion) {
      setUnLockedRegion(data);
      setSelectedRegion(data);
    }
    if (unLockedRegion === data) {
      readRegions([regions.find((reg) => reg.data === data)]);
      setValidableFieldsStatus((prevStatus) => ({
        ...prevStatus,
        [data]: false,
      }));
      setUnLockedRegion();
      setSelectedRegion();
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    const docketNumber = changedValues.docket_number;

    if (docketNumber) {
      setLoadingFields((prev) => ({ ...prev, docket_number: true }));

      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }

      timer.current = setTimeout(async () => {
        await searchAndUpdatePreRegisteredDoc(docketNumber);
        setLoadingFields((prev) => ({ ...prev, docket_number: false }));
      }, 3000);
    }

    const manufactureTime = changedValues.manufacture_time;

    if (manufactureTime) {
      const calculatedLimitTime = calculateLimitTime(manufactureTime);
      if (
        calculatedLimitTime &&
        !calculatedLimitTime.isSame(form.getFieldValue('limit_time'))
      ) {
        form.setFieldValue('limit_time', calculatedLimitTime);
      }
    }

    Object.keys(changedValues).forEach((field) => {
      setValidableFieldsStatus((prevStatus) => ({
        ...prevStatus,
        [field]: false,
      }));
      validableFieldsStatusRef.current = {
        ...validableFieldsStatusRef.current,
        [field]: false,
      };
    });
  };

  useEffect(() => {
    async function loadFile(name) {
      //setLoading(true);
      try {
        const file = await getFile(name, model);
        setSelectedFile(URL.createObjectURL(file.data));
      } catch (error) {
        if (error.response.status === 404) {
          setSelectedFile(fileIcon);
        } else {
          pushNotification({
            message: intl.formatMessage({ id: 'file.loadError' }),
            type: 'error',
            callback: null,
          });
        }
      } finally {
        setLoading(false);
      }
    }
    loadFile(fileName);
    if (initialData) setElementsLoaded(initialData);
  }, [fileName, setLoading, initialData, model, pushNotification, intl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const regionSelectElement = document.querySelector(
        '.region-select-container',
      );
      if (regionSelectElement && !regionSelectElement.contains(event.target)) {
        setSelectedRegion(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (elementsLoaded) {
      setRegions(recreateRegions(elementsLoaded.regions));
      let objkeys = Object.keys(elementsLoaded.form);
      let reloadPreregister = elementsLoaded.form['arriving_time'];
      objkeys.forEach((key) => {
        const processedValue = processFieldValue(key, elementsLoaded.form[key]);
        form.setFieldValue(key, processedValue);
      });
      if (
        elementsLoaded.regionsValue &&
        elementsLoaded.regionsValue['manufacture_time']
      ) {
        const calculatedLimitTime = calculateLimitTime(
          elementsLoaded.regionsValue['manufacture_time'],
        );
        if (calculatedLimitTime) {
          form.setFieldValue('limit_time', calculatedLimitTime);
        }
      }
      if (elementsLoaded.regionsValue && elementsLoaded.regions.length) {
        elementsLoaded.regions.forEach((reg) => {
          const dType = reg.data;
          const value = elementsLoaded.regionsValue[dType];
          if (!reloadPreregister & (dType === 'docket_number')) {
            searchAndUpdatePreRegisteredDoc(value);
          }
          const processedValue = processFieldValue(
            dType,
            elementsLoaded.regionsValue[dType],
          );
          form.setFieldValue(dType, processedValue);
        });
        validateInitialData(form.getFieldsValue(true));
        setLoading(false);
      } else if (
        elementsLoaded.regionsValue &&
        !elementsLoaded.regions.length
      ) {
        Object.keys(elementsLoaded.regionsValue).forEach((dType) => {
          const value = elementsLoaded.regionsValue[dType];
          if (!reloadPreregister & (dType === 'docket_number')) {
            searchAndUpdatePreRegisteredDoc(value);
          }
          const processedValue = processFieldValue(
            dType,
            elementsLoaded.regionsValue[dType],
          );
          form.setFieldValue(dType, processedValue);
        });
      } else {
        readMandatoryRegions();
      }
    }
  }, [
    elementsLoaded,
    form,
    mode,
    readMandatoryRegions,
    searchAndUpdatePreRegisteredDoc,
    setLoading,
    validateInitialData,
  ]);

  return (
    <div className="container" style={{ backgroundColor: 'white' }}>
      <div className="row">
        <div className="col-xs-12">
          <div style={{ padding: '2em' }}>
            {header}

            <Form
              layout="vertical"
              form={form}
              onFinish={() => {
                const rawData = form.getFieldsValue(true);
                const parsedData = formatFormData(rawData);
                onFinish(
                  regions,
                  { ...parsedData, camFileName: camImageName || '' },
                  currentFileName,
                );
              }}
              onValuesChange={handleValuesChange}
            >
              <div className="row" style={{ marginBottom: '1.5em' }}>
                {topFormFields}
              </div>

              <div className="row gap-1">
                <div className="col-xs-12 col-md-9">
                  <div style={{ display: 'flex', marginBottom: '2em' }}>
                    {!isXlsxFile && !isAssayedAndLotified && regions?.length ? (
                      <AccionaButton
                        msg={intl.formatMessage({
                          id: 'pages.quality.ocr.scannedDocuments.create.delivery.scannedDocumentsLayout.moveAllRegions',
                        })}
                        type={moveAllRegions ? 'primary' : 'default'}
                        style={{
                          height: 35,
                          fontSize: '0.9em',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '0px 10px',
                        }}
                        onClick={() => {
                          setMoveAllRegions((prev) => !prev);
                        }}
                        classIcon={`fas fa-${
                          !moveAllRegions ? 'lock' : 'unlock-alt'
                        }`}
                      />
                    ) : null}
                    {(isANoValidatedAndXlsxDeliveryNote || hasNoRegions) &&
                      !loading && (
                        <div className="row" style={{ marginBottom: '1.5em' }}>
                          <div className="col-xs-12 col-md-6">
                            <Upload
                              maxCount={1}
                              accept={updateDeliveryNotesFileExtensionsButton}
                              customRequest={({ file, onError, onSuccess }) => {
                                createFileOnBackend(
                                  file,
                                  model,
                                  project.id_project,
                                  false,
                                )
                                  .then(({ data }) => {
                                    onSuccess(data, file);
                                    if (data) {
                                      getFile(data.fileName, model).then(
                                        ({ data: dataFile }) => {
                                          setSelectedFile(
                                            URL.createObjectURL(dataFile),
                                          );
                                          setCurrentFileName(data.fileName);
                                        },
                                      );
                                    } else {
                                      setSelectedFile(fileIcon);
                                      setCurrentFileName(fileName);
                                    }
                                  })
                                  .catch(onError);
                              }}
                              onRemove={() => {
                                setSelectedFile(fileIcon);
                                setCurrentFileName(fileName);
                              }}
                            >
                              <AccionaButton
                                msg={intl.formatMessage({
                                  id: `pages.quality.ocr.scannedDocuments.create.delivery.scannedDocumentsLayout.upload.${
                                    model === 'laboratory'
                                      ? 'laboratoryNote'
                                      : 'deliveryNote'
                                  }`,
                                })}
                                type="primary"
                                classIcon="fas fa-file-upload"
                                style={{
                                  height: 35,
                                  fontSize: '0.9em',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  padding: '0px 10px',
                                }}
                              />
                            </Upload>
                          </div>
                        </div>
                      )}
                  </div>
                  <RegionSelect
                    className="region-select-container"
                    regions={regions}
                    constraint
                    onChange={setRegions}
                    selectedRegion={selectedRegion}
                    setSelectedRegion={setSelectedRegion}
                    moveAllRegions={moveAllRegions}
                    unLockedRegion={unLockedRegion}
                    onClickLock={onClickLock}
                    updateMoveAllRegions={updateMoveAllRegions}
                    style={{
                      border: '1px solid #F1F1F1',
                      ...(fileName === 'xlsx.png' || fileName === 'cemosa.png'
                        ? {
                            maxWidth: '100%',
                            minHeight: '1000px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }
                        : {}),
                    }}
                    disableLocks={isXlsxFile || isAssayedAndLotified}
                  >
                    <img
                      alt="region-select-img"
                      style={{ userSelect: 'none' }}
                      id="region-select-canvas"
                      src={selectedFile}
                    />
                  </RegionSelect>
                </div>
                <div className="col-xs-12 col-md-3">
                  <div
                    style={{
                      border: '1px solid #ececec',
                      background: '#ececec',
                    }}
                  >
                    <h4 className={styles.fieldGroupsTitle}>
                      {intl.formatMessage({
                        id: 'pages.quality.ocr.scannedDocuments.create.delivery.scannedDocumentsLayout.mandatoryFields.title',
                      })}
                    </h4>
                    <div style={{ backgroundColor: '#fff' }}>
                      {fieldGroups.mandatory.map((fieldName) => {
                        const fieldConfig = fieldsConfig[fieldName];
                        return (
                          <FieldInput
                            key={fieldName}
                            name={fieldName}
                            labelKey={fieldConfig.labelKey}
                            showManualValidation={fieldConfig.manualValidation}
                            rules={fieldConfig.rules}
                            disabled={fieldConfig.disabled}
                            options={fieldConfig.options}
                            inputType={fieldConfig.inputType}
                            onClickLock={onClickLock}
                            locked={unLockedRegion !== fieldName}
                            validated={validableFieldsStatus[fieldName]}
                            onClickValidate={onClickValidate}
                            loading={!!loadingFields[fieldName]}
                            lockDisabled={fieldConfig.lockDisabled}
                            selectedRegion={selectedRegion}
                            setSelectedRegion={setSelectedRegion}
                          />
                        );
                      })}
                    </div>
                  </div>
                  {fieldGroups.other && (
                    <div
                      style={{
                        border: '1px solid #ececec',
                        background: '#ececec',
                      }}
                    >
                      <h4 className={styles.fieldGroupsTitle}>
                        {intl.formatMessage({
                          id: 'pages.quality.ocr.scannedDocuments.create.delivery.scannedDocumentsLayout.otherFields.title',
                        })}
                        <ReloadIcon
                          reloadFunction={() => {
                            readOtherRegions();
                          }}
                        />
                      </h4>
                      <div
                        style={{
                          backgroundColor: '#fff',
                        }}
                      >
                        {fieldGroups.other.map((fieldName) => {
                          const fieldConfig = fieldsConfig[fieldName];
                          return (
                            <FieldInput
                              key={fieldName}
                              name={fieldName}
                              labelKey={fieldConfig.labelKey}
                              showManualValidation={
                                fieldConfig.manualValidation
                              }
                              rules={fieldConfig.rules}
                              disabled={fieldConfig.disabled}
                              options={fieldConfig.options}
                              inputType={fieldConfig.inputType}
                              onClickLock={onClickLock}
                              locked={unLockedRegion !== fieldName}
                              validated={validableFieldsStatus[fieldName]}
                              onClickValidate={onClickValidate}
                              loading={!!loadingFields[fieldName]}
                              lockDisabled={fieldConfig.lockDisabled}
                              selectedRegion={selectedRegion}
                              setSelectedRegion={setSelectedRegion}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <Divider />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <AccionaButton
                      msg={intl.formatMessage({ id: 'back' })}
                      onClick={onBack}
                      classIcon="icon fas fa-chevron-left"
                      style={{ float: 'right', marginRight: '-20px' }}
                    />
                    <AccionaButton
                      id="saveTemplateBtn"
                      onClick={async () => {
                        const formData = form.getFieldsValue(true);
                        const isValid = await validateInitialData(formData);
                        if (isValid) form.submit();
                      }}
                      msg={intl.formatMessage({ id: 'save' })}
                      classIcon="icon fas fa-check"
                    />
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

ScannedDocumentsLayout.propTypes = {
  initialData: PropTypes.object,
  fieldGroups: PropTypes.shape({
    mandatory: PropTypes.arrayOf(PropTypes.string),
    other: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  formValues: PropTypes.object,
  validableFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  onBack: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  header: PropTypes.node,
  topFormFields: PropTypes.node,
  model: PropTypes.oneOf(['delivery', 'deliveryPC', 'laboratory', 'template'])
    .isRequired,
  isAssayedAndLotified: PropTypes.bool,
};

ScannedDocumentsLayout.defaultProps = {
  initialData: {},
  formValues: {},
  mode: 'create',
  header: undefined,
  topFormFields: undefined,
  model: 'delivery',
  isAssayedAndLotified: false,
};

export default ScannedDocumentsLayout;
